import {useCallback, useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';

import {
  AspectRatio,
  Box,
  Heading,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein';
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer';

import RangeProductTile from '@amblique/king-living/app/components/range-product-tile';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import {
  ACCOUNT_LANDING_LAYOUT_CONTEXT,
  ACCOUNT_LANDING_SUGGESTED_PRODUCT_LIMIT,
  ACCOUNT_LANDING_SUGGESTED_SECTION_GAP,
} from '@amblique/king-living/app/constants/account';
import {MOCK_EINSTEIN_RECOMMENDATIONS} from '@amblique/king-living/app/constants/carousel-einstein-mock';
import {
  resolveGa4ListMeta,
  useGa4TileSelect,
  VIEW_ITEM_LIST,
  useGa4ViewItemList,
} from '@amblique/king-living/app/analytics/ga4';
import {
  getProductTilesDesktopHeroMaxWidthPx,
  PRODUCT_TILE_FRAME_RATIO,
  resolveProductTilesDesktopColumnCount,
} from '@amblique/king-living/app/constants/product-tiles-layout';
import {resolveContentfulSectionHeading} from '@amblique/king-living/app/contentful/helpers/section-heading';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {usePageContext} from '@amblique/king-living/app/hooks/contentful';
import {useContentfulCarouselProducts} from '@amblique/king-living/app/hooks/use-contentful-carousel-products';

const TILE_SKELETON_COUNT = 4;

const PRODUCT_TILES_CAROUSEL_HEADING_GAP = {base: 6, lg: 11};

function ProductTileSkeleton({desktopTileMaxWidthPx}) {
  return (
    <Box w="full">
      <AspectRatio
        maxW={
          desktopTileMaxWidthPx != null ?
            {base: '100%', lg: `${desktopTileMaxWidthPx}px`} :
            {base: '100%', lg: '317px'}
        }
        ratio={PRODUCT_TILE_FRAME_RATIO}
        w="full"
      >
        <Skeleton height="100%" width="100%" />
      </AspectRatio>
      <Stack mt={3} spacing={2}>
        <Skeleton height="14px" width="72%" />
        <Skeleton height="12px" width="55%" />
        <Skeleton height="18px" width="38%" />
      </Stack>
    </Box>
  );
}

ProductTileSkeleton.propTypes = {
  desktopTileMaxWidthPx: PropTypes.number,
};

/** Grid + carousel — shared Range PLP tile (badge, price, image chrome). */
function renderProductTiles(
  products,
  {
    testIdPrefix = 'product-tiles',
    onTileClick,
    desktopTileMaxWidthPx,
    isInCarouselSlide = false,
    staticHeroImage = false,
  } = {}
) {
  return products.map((product, index) => {
    const id = product.id || product.productId;

    return (
      <RangeProductTile
        key={id}
        data-testid={`${testIdPrefix}-tile-${id}`}
        desktopTileMaxWidthPx={desktopTileMaxWidthPx}
        isInCarouselSlide={isInCarouselSlide}
        staticHeroImage={staticHeroImage}
        product={product}
        tileIndex={index}
        onClick={onTileClick ? () => onTileClick(product, index) : undefined}
      />
    );
  });
}

/** Shared hook — fetches Einstein recommendations and hydrates products via SCAPI */
function useEinsteinProducts(recommenderId) {
  const {product, isProductPage} = usePageContext();
  const ref = useRef();
  const isOnScreen = useIntersectionObserver(ref, {useOnce: true});

  const {
    isLoading: einsteinIsLoading,
    recommendations,
    getRecommendations,
    sendViewReco,
    sendClickReco,
  } = useEinstein();

  // Context product — only on PDP pages. Non-PDP pages pass undefined so Einstein
  // returns general recommendations without personalisation context.
  const contextProducts = useMemo(
    () => (isProductPage && product?.id ? [{id: product.id}] : undefined),
    [isProductPage, product?.id]
  );

  const sendViewRecoCallback = useCallback(() => {
    if (!recommendations?.recs?.length) return;

    sendViewReco(
      {recommenderName: recommendations.recommenderName, __recoUUID: recommendations.recoUUID},
      recommendations.recs.map((rec) => ({id: rec.id}))
    );
  }, [recommendations, sendViewReco]);

  const handleTileClick = useCallback(
    (clickedProduct) => {
      if (!recommendations?.recs?.length) return;

      sendClickReco(
        {recommenderName: recommendations.recommenderName, __recoUUID: recommendations.recoUUID},
        clickedProduct
      );
    },
    [recommendations, sendClickReco]
  );

  // getRecommendations intentionally omitted from deps — its reference changes every render
  // (same pattern as recommended-products/index.jsx). Including it causes an infinite fetch loop.
  useEffect(() => {
    getRecommendations(recommenderId, contextProducts);
  }, [recommenderId, contextProducts]); // getRecommendations omitted — reference changes every render

  useEffect(() => {
    if (isOnScreen && recommendations?.recs?.length) {
      sendViewRecoCallback();
    }
  }, [isOnScreen, recommendations, sendViewRecoCallback]);

  // Use live rec IDs when available; fall back to mock in sandbox when Einstein returns nothing.
  const recIds = useMemo(() => {
    const recs = recommendations?.recs?.length ? recommendations.recs : MOCK_EINSTEIN_RECOMMENDATIONS.recs;

    return recs.map((rec) => rec.id);
  }, [recommendations]);

  const {products, isLoading: productsIsLoading} = useContentfulCarouselProducts({
    productIds: recIds,
  });

  return {
    ref,
    products,
    isLoading: einsteinIsLoading || productsIsLoading,
    handleTileClick,
  };
}

function useProductTilesSelect(headingText, einsteinRecommenderId, einsteinTileClick) {
  const listMeta = useMemo(() => resolveGa4ListMeta(headingText), [headingText]);
  const afterSelect = einsteinRecommenderId ? einsteinTileClick : undefined;

  return useGa4TileSelect(listMeta, afterSelect);
}

/**
 * Grid layout — renders tiles in a SimpleGrid.
 * Handles both Einstein (einsteinRecommenderId) and static (productIds / categoryId) data sources.
 */
function ProductTilesGrid({component, headingText, isBusy, styles}) {
  const {einsteinRecommenderId, categoryId, numberItemsPerView} = component;
  const productIdList = component.productIds ?? component.productIDs;

  const desktopColumnCount = resolveProductTilesDesktopColumnCount(numberItemsPerView);
  const desktopTileMaxWidthPx = getProductTilesDesktopHeroMaxWidthPx(desktopColumnCount);

  // Einstein data source
  const {ref: einsteinRef, products: einsteinProducts, isLoading: einsteinIsLoading, handleTileClick} =
    useEinsteinProducts(einsteinRecommenderId ?? '__disabled__');

  // Static/category data source — disabled when Einstein is active (Rules of Hooks: called unconditionally)
  const {products: staticProducts, isLoading: staticIsLoading} = useContentfulCarouselProducts(
    einsteinRecommenderId ? {} : {productIds: productIdList, categoryId}
  );

  const products = einsteinRecommenderId ? einsteinProducts : staticProducts;
  const isLoading = einsteinRecommenderId ? einsteinIsLoading : staticIsLoading;
  const showSkeleton = isBusy || isLoading;

  const visibilityRef = useRef();
  const isOnScreen = useIntersectionObserver(visibilityRef, {useOnce: false});
  const {trackNewProducts} = useGa4ViewItemList(VIEW_ITEM_LIST.PRODUCT_CAROUSEL);
  const handleTileSelect = useProductTilesSelect(headingText, einsteinRecommenderId, handleTileClick);

  useEffect(() => {
    if (!isOnScreen || showSkeleton || !products.length) {
      return;
    }

    void trackNewProducts(products);
  }, [isOnScreen, showSkeleton, products, trackNewProducts]);

  const setVisibilityRef = useCallback(
    (node) => {
      visibilityRef.current = node;

      if (einsteinRecommenderId && einsteinRef) {
        einsteinRef.current = node;
      }
    },
    [einsteinRecommenderId, einsteinRef]
  );

  if (!showSkeleton && products.length === 0) return null;

  return (
    <Box
      ref={setVisibilityRef}
      className="contentful-product-tiles__grid"
      maxW="100%"
      minW={0}
      w="full"
    >
      <Box {...styles.productList}>
        <SimpleGrid
          {...styles.productGrid}
          columns={{base: 2, md: 3, lg: desktopColumnCount}}
        >
          {showSkeleton ?
            Array.from({length: TILE_SKELETON_COUNT}).map((_, index) => (
              <ProductTileSkeleton key={index} desktopTileMaxWidthPx={desktopTileMaxWidthPx} />
            )) :
            renderProductTiles(products, {
              desktopTileMaxWidthPx,
              onTileClick: handleTileSelect,
            })}
        </SimpleGrid>
      </Box>
    </Box>
  );
}

ProductTilesGrid.propTypes = {
  component: PropTypes.object.isRequired,
  headingText: PropTypes.string,
  isBusy: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};

// ---------------------------------------------------------------------------
// Carousel layout
// ---------------------------------------------------------------------------

/**
 * Carousel layout — renders tiles in a horizontal Swiper.
 * Handles both Einstein (einsteinRecommenderId) and static (productIds / categoryId) data sources.
 */
function ProductTilesCarousel({component, headingText, isBusy, layoutContext}) {
  const {einsteinRecommenderId, categoryId, numberItemsPerView} = component;
  const productIdList = component.productIds ?? component.productIDs;

  const isAccountLanding = layoutContext === ACCOUNT_LANDING_LAYOUT_CONTEXT;
  const desktopColumnCount = resolveProductTilesDesktopColumnCount(numberItemsPerView);
  // Account landing: fluid slide width (Swiper 1.3 mobile / 3 desktop). Other carousels: fixed hero maxW.
  const desktopTileMaxWidthPx = isAccountLanding ?
    undefined :
    getProductTilesDesktopHeroMaxWidthPx(desktopColumnCount);

  const swiperPreset = isAccountLanding ?
    'account-landing-product-tiles' :
    desktopColumnCount === 3 ?
      'product-tiles-carousel-3' :
      'product-tiles-carousel';
  const specificClassName = isAccountLanding ?
    'contentful-product-tiles__carousel--account-landing' :
    desktopColumnCount === 3 ?
      'contentful-product-tiles__carousel--3' :
      'contentful-product-tiles__carousel--4';

  // Einstein data source
  const {ref: einsteinRef, products: einsteinProducts, isLoading: einsteinIsLoading, handleTileClick} =
    useEinsteinProducts(einsteinRecommenderId ?? '__disabled__');

  // Static/category data source — disabled when Einstein is active (Rules of Hooks: called unconditionally)
  const {products: staticProducts, isLoading: staticIsLoading} = useContentfulCarouselProducts(
    einsteinRecommenderId ? {} : {productIds: productIdList, categoryId}
  );

  const products = einsteinRecommenderId ? einsteinProducts : staticProducts;
  const displayProducts = isAccountLanding ?
    products.slice(0, ACCOUNT_LANDING_SUGGESTED_PRODUCT_LIMIT) :
    products;
  const isLoading = einsteinRecommenderId ? einsteinIsLoading : staticIsLoading;
  const showSkeleton = isBusy || isLoading;
  const skeletonCount = isAccountLanding ? ACCOUNT_LANDING_SUGGESTED_PRODUCT_LIMIT : TILE_SKELETON_COUNT;

  const visibilityRef = useRef();
  const isOnScreen = useIntersectionObserver(visibilityRef, {useOnce: false});
  const {onSwiper} = useGa4ViewItemList(VIEW_ITEM_LIST.PRODUCT_CAROUSEL, {
    isOnScreen,
    products: displayProducts,
    ready: !showSkeleton && displayProducts.length > 0,
  });
  const handleTileSelect = useProductTilesSelect(headingText, einsteinRecommenderId, handleTileClick);

  const setVisibilityRef = useCallback(
    (node) => {
      visibilityRef.current = node;

      if (einsteinRecommenderId && einsteinRef) {
        einsteinRef.current = node;
      }
    },
    [einsteinRecommenderId, einsteinRef]
  );

  if (!showSkeleton && displayProducts.length === 0) return null;

  const tiles = showSkeleton ?
    Array.from({length: skeletonCount}).map((_, index) => (
      <ProductTileSkeleton key={index} desktopTileMaxWidthPx={desktopTileMaxWidthPx} />
    )) :
    renderProductTiles(displayProducts, {
      desktopTileMaxWidthPx,
      isInCarouselSlide: true,
      testIdPrefix: isAccountLanding ? 'account-suggested' : 'product-tiles-carousel',
      onTileClick: handleTileSelect,
    });

  return (
    <Box
      ref={setVisibilityRef}
      className={`contentful-product-tiles__carousel ${specificClassName}`}
      overflow={{base: 'hidden', lg: 'unset'}}
      w="full"
    >
      <Swiper
        className="contentful-product-tiles__swiper swiper-product-tiles-carousel"
        paddingLeft={isAccountLanding ? 0 : {base: '20px', lg: 0}}
        presets={swiperPreset}
        variant={swiperPreset}
        onSwiper={onSwiper}
      >
        {tiles}
      </Swiper>
    </Box>
  );
}

ProductTilesCarousel.propTypes = {
  component: PropTypes.object.isRequired,
  headingText: PropTypes.string,
  isBusy: PropTypes.bool,
  layoutContext: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Shared header
// ---------------------------------------------------------------------------

function SectionHeader({headingText, descriptionText, layoutContext, styles}) {
  const showHeading = Boolean(headingText);
  const showDescription = Boolean(descriptionText);
  const isAccountLanding = layoutContext === ACCOUNT_LANDING_LAYOUT_CONTEXT;
  const sectionHeadingClassName = [
    'contentful-product-tiles__section-heading',
    isAccountLanding && 'account-landing__section-heading'
  ].filter(Boolean).join(' ');
  const sectionTitleClassName = [
    'contentful-product-tiles__section-title',
    isAccountLanding && 'account-landing__suggested-title'
  ].filter(Boolean).join(' ');

  if (!showHeading && !showDescription) return null;

  return (
    <Stack align="stretch" spacing={3} w="full">
      {showHeading && (
        <Box className={sectionHeadingClassName} {...styles.sectionHeading}>
          <Heading className={sectionTitleClassName} {...styles.sectionTitle}>{headingText}</Heading>
        </Box>
      )}
      {showDescription && (
        <Text color="brandColors.greyscale90" maxW="100%" w="full" whiteSpace="pre-line">
          {descriptionText}
        </Text>
      )}
    </Stack>
  );
}

SectionHeader.propTypes = {
  headingText: PropTypes.string,
  descriptionText: PropTypes.string,
  layoutContext: PropTypes.string,
  styles: PropTypes.object.isRequired,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @override
 * Product Tiles Contentful model — renders a product tile grid or carousel sourced from one of:
 *   1. Einstein recommendations   (einsteinRecommenderId field)
 *   2. Explicit product IDs       (productIds / productIDs field)
 *   3. Category search            (categoryId field)
 *
 * The `appearance` field selects the layout (both use `RangeProductTile` — shared PLP chrome):
 *   - 'Grid'     → ProductTilesGrid     (SimpleGrid, default)
 *   - 'Carousel' → ProductTilesCarousel (Swiper, product-tiles-carousel preset)
 *
 * `numberItemsPerView` (`'3'` / `'3.2'` vs `'4.2'` / unset) controls **desktop** columns (3 vs 4) and
 * hero slide width at lg (**424px** vs **317px**) for both Grid and Carousel — see `product-tiles-layout.js`.
 *
 * @param {Object} props
 * @param {Contentful.ProductTiles} props.component - ProductTiles component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 */
export const ProductTiles = ({
  component,
  headingFallback,
  isComplete = true,
  isFetching = false,
  layoutContext,
  ...props
}) => {
  const styles = useMultiStyleConfig('ProductRange', {});
  const {description, cta, appearance} = component;

  const isAccountLanding = layoutContext === ACCOUNT_LANDING_LAYOUT_CONTEXT;
  const headingText = resolveContentfulSectionHeading(
    component,
    isAccountLanding ? headingFallback : undefined
  );
  const descriptionText = typeof description === 'string' ? description.trim() : '';
  const showCta = Boolean(cta?.url || cta?.linkText);
  const isBusy = !isComplete && isFetching;

  // Default to 'Grid' when `appearance` is not configured in Contentful
  const isCarousel = isAccountLanding || (appearance || 'Grid') === 'Carousel';

  return (
    <Box
      className="contentful-product-tiles__root"
      {...styles.container}
      {...props}
      gap={
        isAccountLanding ?
          {base: 6, lg: ACCOUNT_LANDING_SUGGESTED_SECTION_GAP} :
          PRODUCT_TILES_CAROUSEL_HEADING_GAP
      }
      {...(isCarousel ?
        {overflow: 'hidden'} :
        {
          /* `productList` uses negative horizontal margin on mobile — clip bleed so the page/body
           * does not gain horizontal scroll (see ProductRange `productList.mx` base -20px). */
          overflowX: {base: 'hidden', md: 'visible'},
          overflowY: 'visible',
        })}
      maxW="1440px"
      mx="auto"
      w="full"
    >
      <SectionHeader
        descriptionText={descriptionText}
        headingText={headingText}
        layoutContext={layoutContext}
        styles={styles}
      />

      {isCarousel ?
        <ProductTilesCarousel component={component} headingText={headingText} isBusy={isBusy} layoutContext={layoutContext} /> :
        <ProductTilesGrid component={component} headingText={headingText} isBusy={isBusy} styles={styles} />}

      {showCta && !isCarousel && (
        <Box
          {...styles.ctaButtonContainer}
          className={
            ['contentful-product-tiles__cta', styles.ctaButtonContainer?.className].filter(Boolean).join(' ')
          }
        >
          <Skeleton isLoaded={!isBusy}>
            <Box {...styles.ctaButton} as="span" display="inline-flex">
              <ContentfulLink component={cta} />
            </Box>
          </Skeleton>
        </Box>
      )}
    </Box>
  );
};

ProductTiles.displayName = 'ProductTiles';
ProductTiles.propTypes = {
  component: PropTypes.object.isRequired,
  headingFallback: PropTypes.string,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  layoutContext: PropTypes.string,
};
